/**
 * TvMonitorPage — monitor de demanda en vivo para televisor (/tv/monitor).
 *
 * Es la pantalla del monitor en vivo repartida en tres vistas que se turnan:
 *
 *   1. EMBUDO   dónde está cada paciente ahora y cuánto cupo queda.
 *   2. PISO     la misma foto, pero en volumen: una silueta por persona.
 *   3. SALAS    el detalle por sala y qué salas están sin cupo.
 *
 * Todo sale del documento agregado `metricas/funnelEnVivo`, que escribe el cron
 * `reconciliarCuposEnVivo` cada minuto. Por eso la cabecera muestra la EDAD del
 * dato: en un televisor encendido todo el día, un tablero congelado que parece
 * vivo engaña más que uno apagado.
 *
 * Frente al monitor del portal falta a propósito una cosa: los botones de
 * simular y refrescar, que no tienen sentido sin operador.
 */
import { useMemo } from 'react';
import { ClipboardList, Hourglass, RadioTower, Video } from 'lucide-react';

import { useCollection, useDocument } from '../../hooks/useFirestore';
import { useModoTV } from '../../hooks/useModoTV';
import { TvShell } from '../../components/tv/TvShell';
import { PisoTopDown } from '../../components/piso/PisoTopDown';
import {
  Panel, FranjaKpis, Etapas, ListaAlertas, TablaTv, type Alerta, type TonoKpi,
} from '../../components/tv/piezas';
import type { Sala } from '../../types';
import s from './TvMonitorPage.module.css';

const VISTAS = ['Embudo en vivo', 'Piso en vivo', 'Salas'] as const;

/** El cron corre cada 60 s pero, sin cambios, solo reescribe el resumen cada
 *  5 min; por encima de esto el cron dejó de correr y el dato ya no es "en vivo". */
const DATO_VIEJO_S = 420;

interface PorSalaEntry {
  enEspera?: number;
  enAtencion?: number;
  enTriage?: number;
  cupoDisponible?: number;
  capacidadDinamica?: number;
  cupoDinamicoDisponible?: number;
  medicosEnTurno?: number;
  medicosLibresAhora?: number;
  motivoSinCupo?: string;
}

interface FunnelEnVivo {
  conectados?: number;
  enTriage?: number;
  enEspera?: number;
  enAtencion?: number;
  totalOcupado?: number;
  salasActivas?: number;
  porSala?: Record<string, PorSalaEntry>;
  actualizadoEn?: { toMillis?: () => number } | null;
}

const MOTIVOS: Record<string, string> = {
  sin_medicos: 'sin médicos',
  tope: 'saturada',
  cerrada: 'cerrada',
};

export default function TvMonitorPage() {
  const tv = useModoTV({ vistas: VISTAS, claveConfig: 'tv.monitor.config' });

  const { data: funnel } = useDocument<FunnelEnVivo>('metricas', 'funnelEnVivo');
  const { data: salas } = useCollection<Sala>('salas');

  const nombreSala = useMemo(
    () => new Map((salas ?? []).map((x) => [x.id, x.nombre])),
    [salas],
  );

  // `tv.tick` fuerza el recálculo de la edad del dato al ritmo configurado.
  const m = useMemo(() => {
    const conectados = funnel?.conectados ?? 0;
    const enTriage = funnel?.enTriage ?? 0;
    const enEspera = funnel?.enEspera ?? 0;
    const enAtencion = funnel?.enAtencion ?? 0;

    const porSala = Object.entries(funnel?.porSala ?? {})
      .map(([id, v]) => ({
        id,
        nombre: nombreSala.get(id) ?? id,
        enTriage: v.enTriage ?? 0,
        enEspera: v.enEspera ?? 0,
        enAtencion: v.enAtencion ?? 0,
        cupo: v.cupoDisponible ?? 0,
        cupoDinamico: v.cupoDinamicoDisponible ?? 0,
        enTurno: v.medicosEnTurno ?? 0,
        libres: v.medicosLibresAhora ?? 0,
        motivo: v.motivoSinCupo ?? 'ok',
      }))
      .sort((a, b) => (b.enTriage + b.enEspera + b.enAtencion) - (a.enTriage + a.enEspera + a.enAtencion));

    const ms = funnel?.actualizadoEn?.toMillis?.() ?? 0;
    const edadS = ms ? Math.max(0, Math.round((Date.now() - ms) / 1000)) : null;

    return {
      conectados, enTriage, enEspera, enAtencion,
      total: funnel?.totalOcupado ?? enTriage + enEspera + enAtencion,
      salasActivas: funnel?.salasActivas ?? porSala.length,
      cupoTotal: porSala.reduce((acc, x) => acc + x.cupo, 0),
      porSala,
      edadS,
      viejo: edadS !== null && edadS > DATO_VIEJO_S,
      sinDato: edadS === null,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [funnel, nombreSala, tv.tick]);

  // Mismos colores e iconos que /admin/monitor: es la misma información mirada
  // desde otra pantalla, no otro tablero.
  const etapas = useMemo(() => ([
    { clave: 'conectado', etiqueta: 'Conectados', valor: m.conectados, color: '#0ea5e9', icono: <RadioTower size={16} /> },
    { clave: 'triage', etiqueta: 'En triage', valor: m.enTriage, color: '#8b5cf6', icono: <ClipboardList size={16} /> },
    {
      clave: 'espera', etiqueta: 'En espera', valor: m.enEspera, color: '#f59e0b', icono: <Hourglass size={16} />,
      cuelloBotella: m.enEspera > 0 && m.cupoTotal === 0,
    },
    { clave: 'atencion', etiqueta: 'En atención', valor: m.enAtencion, color: '#3b82f6', icono: <Video size={16} /> },
  ]), [m]);

  const alertas = useMemo<Alerta[]>(() => {
    const out: Alerta[] = [];
    if (m.sinDato) {
      out.push({
        id: 'sin-dato', nivel: 'critico', texto: 'Sin datos del monitor',
        detalle: 'El recálculo de cupos no ha escrito todavía',
      });
    } else if (m.viejo) {
      out.push({
        id: 'dato-viejo', nivel: 'critico',
        texto: `Datos con ${Math.round((m.edadS ?? 0) / 60)} min de retraso`,
        detalle: 'El recálculo de cupos corre cada minuto',
      });
    }
    // Cuello de botella: hay cola y no queda un solo cupo en todo el sistema.
    if (m.enEspera > 0 && m.cupoTotal === 0) {
      out.push({
        id: 'sin-cupo', nivel: 'critico',
        texto: `${m.enEspera} en espera y cero cupos disponibles`,
      });
    }
    for (const sala of m.porSala.filter((x) => x.motivo === 'sin_medicos' && x.enEspera + x.enTriage > 0).slice(0, 3)) {
      out.push({
        id: `sm-${sala.id}`, nivel: 'aviso',
        texto: `${sala.nombre}: sin médicos con ${sala.enEspera} en espera`,
      });
    }
    for (const sala of m.porSala.filter((x) => x.motivo === 'tope').slice(0, 2)) {
      out.push({ id: `tope-${sala.id}`, nivel: 'aviso', texto: `${sala.nombre}: saturada, sin cupo` });
    }
    return out;
  }, [m]);

  const frescura = m.sinDato
    ? 'esperando primer dato…'
    : (m.edadS ?? 0) < 90 ? `hace ${m.edadS} s` : `hace ${Math.round((m.edadS ?? 0) / 60)} min`;

  const contexto = (
    <>
      <span className={`${s.pulso} ${m.viejo || m.sinDato ? s.pulsoViejo : ''}`}>
        <span className={s.pulsoPunto} />
        {m.viejo || m.sinDato ? 'datos desactualizados' : 'en vivo'} · {frescura}
      </span>
      <span>{m.total} pacientes en el sistema · {m.salasActivas} salas activas</span>
    </>
  );

  const tonoCupo: TonoKpi = m.cupoTotal === 0 ? 'critico' : m.cupoTotal < 5 ? 'aviso' : 'bien';

  return (
    <TvShell titulo="Demanda en vivo" vistas={VISTAS} tv={tv} contexto={contexto}>
      {tv.vistaActual === 0 && (
        <div className={s.vistaEmbudo}>
          <FranjaKpis
            items={[
              { etiqueta: 'Conectados', valor: m.conectados, apoyo: 'navegando teleconsulta' },
              { etiqueta: 'En triage', valor: m.enTriage, apoyo: 'reservando cupo' },
              {
                etiqueta: 'En espera', valor: m.enEspera,
                // Cola vacía es el estado corriente, no un logro: sin gente en
                // espera la celda se queda neutra en vez de encenderse en verde.
                tono: m.enEspera === 0 ? 'normal' : m.cupoTotal === 0 ? 'critico' : 'aviso',
                apoyo: 'en cola de atención',
              },
              { etiqueta: 'En atención', valor: m.enAtencion, apoyo: 'con médico' },
              { etiqueta: 'Cupo disponible', valor: m.cupoTotal, tono: tonoCupo, apoyo: 'suma de todas las salas' },
            ]}
          />

          <div className={s.embudoAbajo}>
            <Panel titulo="Embudo de atención" extra={`${m.total} en total`}>
              <Etapas etapas={etapas} />
            </Panel>
            <Panel titulo="Requiere atención">
              <ListaAlertas alertas={alertas} />
            </Panel>
          </div>
        </div>
      )}

      {tv.vistaActual === 1 && (
        <div className={s.vistaPiso}>
          <Panel titulo="Vista de piso en vivo" extra="top-down">
            <PisoTopDown etapas={etapas} className={s.pisoTv} />
          </Panel>
        </div>
      )}

      {tv.vistaActual === 2 && (
        <div className={s.vistaSalas}>
          <Panel titulo="Detalle por sala" extra={`${m.porSala.length} salas`}>
            {m.porSala.length > 0 ? (
              <TablaTv
                columnas={[
                  { clave: 'sala', titulo: 'Sala' },
                  { clave: 'medicos', titulo: 'Turno / libres', numerica: true },
                  { clave: 'triage', titulo: 'Triage', numerica: true },
                  { clave: 'espera', titulo: 'Espera', numerica: true },
                  { clave: 'atencion', titulo: 'Atención', numerica: true },
                  { clave: 'cupo', titulo: 'Cupo', numerica: true },
                  { clave: 'dinamico', titulo: 'Cupo dinámico', numerica: true },
                ]}
                filas={m.porSala.slice(0, 10).map((x) => ({
                  sala: (
                    <span>
                      {x.nombre}
                      {MOTIVOS[x.motivo] && <span className={s.motivo}> · {MOTIVOS[x.motivo]}</span>}
                    </span>
                  ),
                  medicos: `${x.enTurno} / ${x.libres}`,
                  triage: x.enTriage,
                  espera: x.enEspera,
                  atencion: x.enAtencion,
                  cupo: <span className={x.cupo === 0 ? s.cero : s.ok}>{x.cupo}</span>,
                  dinamico: <span className={x.cupoDinamico === 0 ? s.cero : s.ok}>{x.cupoDinamico}</span>,
                }))}
              />
            ) : (
              <span className={s.vacio}>
                Sin datos de salas todavía. El monitor se llena cuando el recálculo de cupos corre y hay salas activas.
              </span>
            )}
          </Panel>
        </div>
      )}
    </TvShell>
  );
}
